import { useState, useCallback } from "react";
import { AffiliationList, type AffiliationEntry } from "./AffiliationList";
import { useFetchAffiliationRoles } from "@core/features/affiliations";
import { useFetchOrganizations } from "@core/features/organization";
import type { OptionType } from "@themes/pollinator/ui/Select";

export type Props = {
  /** Field name prefix for the inputs */
  name: string;
  /** Current value - array of org/role entries */
  value?: AffiliationEntry[];
  /** Change handler - receives updated entries array */
  onChange?: (value: AffiliationEntry[]) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Maximum number of affiliation entries */
  maxItems?: number;
  /** Additional CSS classes */
  className?: string;
};

/**
 * Container component for AffiliationList that handles data fetching.
 * Fetches organization and role options from the API.
 */
export function AffiliationListContainer({
  name,
  value = [],
  onChange,
  onBlur,
  error,
  disabled,
  maxItems = 1,
  className,
}: Props) {
  // Fetch organizations
  const { options: orgOptions, isLoading: orgsLoading } =
    useFetchOrganizations();

  // Fetch affiliation roles
  const { roles, isLoading: rolesLoading } = useFetchAffiliationRoles();

  // Track user-created options
  const [createdOrgs, setCreatedOrgs] = useState<OptionType[]>([]);

  // Convert roles to options format (value must be string)
  const roleOptions: OptionType[] = roles.map((role) => ({
    label: role.name,
    value: String(role.id),
  }));

  // Combine loaded and created options
  const allOrgOptions = [...orgOptions, ...createdOrgs];

  const handleCreateOrganization = useCallback((name: string) => {
    const newOption: OptionType = {
      label: name,
      value: `new-${name.toLowerCase().replace(/\s+/g, "-")}`,
      data: { isNew: true, name },
    };
    setCreatedOrgs((prev) => {
      const exists = prev.some((opt) => opt.value === newOption.value);
      return exists ? prev : [...prev, newOption];
    });
  }, []);

  return (
    <AffiliationList
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      disabled={disabled}
      maxItems={maxItems}
      className={className}
      organizationOptions={allOrgOptions}
      roleOptions={roleOptions}
      isLoading={orgsLoading || rolesLoading}
      onCreateOrganization={handleCreateOrganization}
    />
  );
}

export default AffiliationListContainer;
